'use client';

import { useQuery } from 'convex-helpers/react/cache/hooks';
import { api } from 'convex/_generated/api';
import type { Id } from 'convex/_generated/dataModel';
import { useCreateUserPlant, useDeleteUserPlant, useUpdateUserPlant } from '@api/plants';
import { usePlantsStore } from '@shared/store/usePlantsStore';
import {
  canEditPlant,
  getDefaultCategory,
  getPlantName,
  getVisiblePlants,
  type Plant,
} from '@modules/plants/business_logic';
import { text as plantsText } from '@modules/plants/text_business_logic';
import { ComponentType, type FormEvent, useMemo } from 'react';

export interface PlantsProps {
  isLoading: boolean;
  isSaving: boolean;
  plants: Plant[];
  categories: string[];
  visiblePlants: Plant[];
  searchQuery: string;
  selectedCategory: string | null;
  onSearchQueryChange: (query: string) => void;
  onSelectedCategoryChange: (category: string | null) => void;
  onStartCreating: () => void;
  onStartEditing: (plant: Plant) => void;
  onConfirmDelete: (plantId: Id<'userPlants'>) => void;
  canEditPlant: (plant: Plant) => boolean;
  createDialogOpen: boolean;
  editDialogOpen: boolean;
  deleteDialogOpen: boolean;
  draftName: string;
  draftEmoji: string;
  draftCategory: string;
  onDraftNameChange: (name: string) => void;
  onDraftEmojiChange: (emoji: string) => void;
  onDraftCategoryChange: (category: string) => void;
  onCreateSubmit: (e: FormEvent) => void;
  onCreateCancel: () => void;
  onEditSubmit: (e: FormEvent) => void;
  onEditCancel: () => void;
  plantToDeleteName: string | undefined;
  onDeleteConfirm: () => void;
  onDeleteCancel: () => void;
}

interface PlantsContainerProps {
  View: ComponentType<PlantsProps>;
  onError?: (message: string) => void;
}

export function PlantsContainer({ View, onError }: PlantsContainerProps) {
  const plantsQuery = useQuery(api.plants.queries.getAllPlants, {});
  const categoriesQuery = useQuery(api.plants.queries.getPlantCategories, {});

  const createDialogOpen = usePlantsStore((state) => state.createDialogOpen);
  const editDialogOpen = usePlantsStore((state) => state.editDialogOpen);
  const deleteDialogOpen = usePlantsStore((state) => state.deleteDialogOpen);
  const editingPlantId = usePlantsStore((state) => state.editingPlantId);
  const plantToDelete = usePlantsStore((state) => state.plantToDelete);
  const draftName = usePlantsStore((state) => state.draftName);
  const draftEmoji = usePlantsStore((state) => state.draftEmoji);
  const draftCategory = usePlantsStore((state) => state.draftCategory);
  const searchQuery = usePlantsStore((state) => state.searchQuery);
  const selectedCategory = usePlantsStore((state) => state.selectedCategory);
  const isSaving = usePlantsStore((state) => state.isSaving);

  const setIsSaving = usePlantsStore((state) => state.setIsSaving);
  const setSearchQuery = usePlantsStore((state) => state.setSearchQuery);
  const setSelectedCategory = usePlantsStore((state) => state.setSelectedCategory);
  const startCreating = usePlantsStore((state) => state.startCreating);
  const cancelCreating = usePlantsStore((state) => state.cancelCreating);
  const startEditing = usePlantsStore((state) => state.startEditing);
  const cancelEditing = usePlantsStore((state) => state.cancelEditing);
  const confirmDelete = usePlantsStore((state) => state.confirmDelete);
  const cancelDelete = usePlantsStore((state) => state.cancelDelete);
  const setDraftName = usePlantsStore((state) => state.setDraftName);
  const setDraftEmoji = usePlantsStore((state) => state.setDraftEmoji);
  const setDraftCategory = usePlantsStore((state) => state.setDraftCategory);

  const createUserPlant = useCreateUserPlant();
  const updateUserPlant = useUpdateUserPlant();
  const deleteUserPlant = useDeleteUserPlant();

  const isLoading = plantsQuery === undefined || categoriesQuery === undefined;
  const plantList = plantsQuery ?? [];
  const categories = categoriesQuery ?? [];
  const defaultCategory = getDefaultCategory(categories);

  const visiblePlants = useMemo(
    () => getVisiblePlants(plantList, searchQuery, selectedCategory),
    [plantList, searchQuery, selectedCategory]
  );

  const plantToDeleteName = getPlantName(
    plantList,
    plantToDelete ? String(plantToDelete) : null
  );

  const handleStartCreating = () => {
    startCreating(defaultCategory);
  };

  const handleStartEditing = (plant: Plant) => {
    startEditing(plant.id as Id<'userPlants'>, plant.name, plant.emoji, plant.category);
  };

  const handleCreateSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!draftName.trim()) return;

    setIsSaving(true);
    try {
      await createUserPlant({
        name: draftName.trim(),
        emoji: draftEmoji,
        category: draftCategory,
      });
      cancelCreating();
    } catch (error) {
      onError?.(plantsText.createErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingPlantId || !draftName.trim()) return;

    setIsSaving(true);
    try {
      await updateUserPlant({
        userPlantId: editingPlantId,
        name: draftName.trim(),
        emoji: draftEmoji,
        category: draftCategory,
      });
      cancelEditing();
    } catch (error) {
      onError?.(plantsText.updateErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!plantToDelete) return;

    setIsSaving(true);
    try {
      await deleteUserPlant({ userPlantId: plantToDelete });
      cancelDelete();
    } catch (error) {
      onError?.(plantsText.deleteErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <View
      isLoading={isLoading}
      isSaving={isSaving}
      plants={plantList}
      categories={categories}
      visiblePlants={visiblePlants}
      searchQuery={searchQuery}
      selectedCategory={selectedCategory}
      onSearchQueryChange={setSearchQuery}
      onSelectedCategoryChange={setSelectedCategory}
      onStartCreating={handleStartCreating}
      onStartEditing={handleStartEditing}
      onConfirmDelete={confirmDelete}
      canEditPlant={canEditPlant}
      createDialogOpen={createDialogOpen}
      editDialogOpen={editDialogOpen}
      deleteDialogOpen={deleteDialogOpen}
      draftName={draftName}
      draftEmoji={draftEmoji}
      draftCategory={draftCategory}
      onDraftNameChange={setDraftName}
      onDraftEmojiChange={setDraftEmoji}
      onDraftCategoryChange={setDraftCategory}
      onCreateSubmit={handleCreateSubmit}
      onCreateCancel={cancelCreating}
      onEditSubmit={handleEditSubmit}
      onEditCancel={cancelEditing}
      plantToDeleteName={plantToDeleteName}
      onDeleteConfirm={handleDeleteConfirm}
      onDeleteCancel={cancelDelete}
    />
  );
}
